/* eslint-disable react/prop-types */

import { surveyStatus } from '@api/surveys';

import { ColumnsIcon, ErrorIcon } from '@utilities/icons';
import { Tag } from '@utilities';

const CREATOR_FILTER_OPTIONS = [
  {
    label: 'Created by Me',
    value: 'created_by_me_all',
  },
  {
    label: 'Created by Others',
    value: 'created_by_others_all',
  },
];

const ALCHEMER_FILTER_OPTIONS = [
  {
    label: 'Creator',
    options: CREATOR_FILTER_OPTIONS,
  },
  {
    label: 'Drafts',
    options: [
      {
        label: 'Drafts',
        value: 'is_draft_item',
      },
    ],
  },
  {
    label: 'Launched',
    options: [
      {
        label: 'Issuing',
        value: 'is_issuing_item',
      },
      {
        label: 'Active',
        value: 'is_active_item',
      },
      {
        label: 'Complete',
        value: 'is_complete_item',
      },
    ],
  },
];

const CALYPSO_FILTER_OPTIONS = [
  {
    label: 'Creator',
    options: CREATOR_FILTER_OPTIONS,
  },
  {
    label: 'Status',
    options: [
      {
        label: 'Fielded',
        value: 'is_fielded_item',
      },
      {
        label: 'In Review',
        value: 'is_in_review_item',
      },
      {
        label: 'Met Sample',
        value: 'is_met_sample_item',
      },
      {
        label: 'Completed',
        value: 'is_completed_item',
      },
      {
        label: 'Draft',
        value: 'is_draft_item',
      },
    ],
  },
];

export const getFilterOptions = (useCalypsoStatus, { excludeDraft = false } = {}) => {
  const source = useCalypsoStatus ? CALYPSO_FILTER_OPTIONS : ALCHEMER_FILTER_OPTIONS;
  if (!excludeDraft) return source;

  return source
    .map((group) => ({
      ...group,
      options: group.options.filter((option) => option.value !== 'is_draft_item'),
    }))
    .filter((group) => group.options.length > 0);
};

const getAlchemerStatus = (status) => {
  switch (status) {
    case surveyStatus.CREATED:
    case surveyStatus.FIELDED:
    case surveyStatus.FIELDING:
    case surveyStatus.PUBLISHING:
    case surveyStatus.PUBLISHED:
      return 'Issuing';
    case surveyStatus.EXCEPTION:
    case surveyStatus.INCOMPLETE:
    case surveyStatus.IN_REVIEW:
    case surveyStatus.LOW_SAMPLE:
      return 'In Review';
    case surveyStatus.MET_SAMPLE:
      return 'Active';
    case surveyStatus.COMPLETED:
      return 'Complete';
    case surveyStatus.DRAFT:
      return 'Draft';
    default:
      return <Tag icon={<ErrorIcon />} label="Error" />;
  }
};

const CALYPSO_STATUS_LABELS = {
  [surveyStatus.CREATED]: 'Created',
  [surveyStatus.FIELDED]: 'Fielded',
  [surveyStatus.FIELDING]: 'Fielding',
  [surveyStatus.PUBLISHING]: 'Publishing',
  [surveyStatus.PUBLISHED]: 'Published',
  [surveyStatus.EXCEPTION]: 'Exception',
  [surveyStatus.INCOMPLETE]: 'Incomplete',
  [surveyStatus.IN_REVIEW]: 'In Review',
  [surveyStatus.LOW_SAMPLE]: 'Low Sample',
  [surveyStatus.MET_SAMPLE]: 'Met Sample',
  [surveyStatus.COMPLETED]: 'Completed',
  [surveyStatus.DRAFT]: 'Draft',
};

const getCalypsoStatus = (status) => {
  return CALYPSO_STATUS_LABELS[status] || <Tag icon={<ErrorIcon />} label="Error" />;
};

const ITEM_COLUMNS = [
  {
    accessor: 'title',
    Header: 'Name',
    width: 250,
  },
  {
    accessor: 'instantSurveyTypeName',
    disableSortBy: true,
    Header: 'Type',
    width: 100,
  },
  // status column inserted here via `ITEM_COLUMNS.toSpliced()`
  {
    Cell: ({ row }) => {
      return row?.original?.isPaused ? <Tag icon={<ColumnsIcon />} tooltip="Paused" /> : null;
    },
    disableSortBy: true,
    Header: 'Tags',
    width: 100,
  },
  {
    accessor: 'creator',
    disableSortBy: true,
    Header: 'Creator',
    width: 100,
  },
  {
    accessor: 'created_datetime',
    Cell: ({ row }) => {
      return new Date(row?.original?.created_datetime).toLocaleDateString('en-us', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    },
    Header: 'Created',
    width: 100,
  },
];

export const getItemColumns = (useCalypsoStatus) => {
  const columns = [...ITEM_COLUMNS];
  columns.splice(2, 0, {
    accessor: 'status',
    Cell: ({ row }) =>
      useCalypsoStatus
        ? getCalypsoStatus(row?.original?.status)
        : getAlchemerStatus(row?.original?.status),
    disableSortBy: true,
    Header: 'Status',
    width: 100,
  });
  return columns;
};
